import type { TicketDri } from '../../types/bpTickets';
import { SELECT_CHEVRON } from './chevron';

export interface AssigneeOption {
  email: string;
  name: string;
}

/**
 * A plain (non-chip) `<select>` for the assigned Business Partner, replacing the static "Business
 * Partner" value in the ticket detail `<dl>`. Not the design-system Select atom: its trigger/item
 * sizing (px-3 py-2.5 trigger, min-h-14 items, body-16-regular) is built for a full-size form
 * field and reads as oversized inside this modal's dense two-column `<dl>` — the same reason TA
 * Cockpit's role-prioritisation support form falls back to native `<select>`s (see CLAUDE.md).
 */
export function AssigneeSelect({
  dri,
  options,
  onChange,
  disabled,
}: {
  dri: TicketDri | null;
  options: AssigneeOption[];
  onChange: (assignee: AssigneeOption) => void;
  disabled?: boolean;
}) {
  // The current assignee may not be in the known-DRI list yet (a Business Partner who has never
  // had a ticket assigned to them before, so they never showed up as an `allDris` group) — keep
  // them selectable rather than silently dropping the select to the first option in the list.
  const known = dri && !options.some((o) => o.email === dri.email) ? [dri, ...options] : options;

  return (
    <select
      value={dri?.email ?? ''}
      disabled={disabled}
      onChange={(e) => {
        const picked = known.find((o) => o.email === e.target.value);
        if (picked) onChange(picked);
      }}
      style={{ backgroundImage: SELECT_CHEVRON, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 2px center', backgroundSize: '10px' }}
      className="w-full appearance-none rounded border-0 bg-transparent py-0.5 pl-0 pr-5 text-sm font-medium text-black disabled:opacity-60"
    >
      {!dri && <option value="">—</option>}
      {known.map((o) => (
        <option key={o.email} value={o.email}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
